import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { LogIn, Eye, EyeOff, Shield, User } from 'lucide-react';
import { useDirectusAuth } from '../../providers/DirectusAuthProvider';
import { useAppDispatch } from '../../hooks/redux';
import { useFormSubmitToast } from '../../hooks/useSubmitToast';
import { navigateTo } from '../../store/slices/navigationSlice';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';
import { LanguageToggle } from './LanguageToggle';
import { ThemeToggle } from '../ui/ThemeToggle';

interface LoginFormProps {
  onSwitchToRegister?: () => void;
  onSwitchToPasswordReset?: () => void;
  onSuccess?: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onSwitchToPasswordReset: _onSwitchToPasswordReset,
  onSuccess
}) => {
  const { login, isLoading, error } = useDirectusAuth();
  const dispatch = useAppDispatch();
  const { executeWithToast } = useFormSubmitToast();
  const { t } = useLanguage();
  const [showPassword, setShowPassword] = useState(false);
  // ✅ NEW: Form submission state guard to prevent accidental submissions during language changes
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ✅ FIXED: Static schema with dynamic error messages to prevent recreation on language change
  // This prevents form re-initialization when user switches language
  const formSchema = z.object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(1, 'Password is required'),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    // ✅ FIXED: Prevent double submission during language changes or rapid clicks
    if (isSubmitting) {
      console.log('⚠️ Form submission already in progress, ignoring duplicate request');
      return;
    }

    setIsSubmitting(true);

    try {
      await executeWithToast(
        async () => {
          await login(values.email, values.password);

          // Ensure navigation to dashboard after successful login
          console.log('✅ Login successful, navigating to dashboard');
          dispatch(navigateTo('dashboard'));

          onSuccess?.();
        },
        {
          loading: t('auth.login.loading'),
          success: t('dashboard.welcomeBack'),
          error: t('error.invalid_credentials')
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl shadow-lg p-8 w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg">
            <Shield className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold text-muted-foreground mb-2">
            {t('login.title')}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t('login.subtitle')}
          </p>
        </div>

        {/* ✅ FIXED: Language and theme toggles moved outside form to prevent event bubbling */}
        <div className="mb-6 flex justify-between items-center">
          <ThemeToggle />
          <LanguageToggle />
        </div>

        {/* Authorization Info */}
        <div className="mb-6 p-4 bg-yellow-50 border border-orange-200 rounded-xl">
          <div className="flex items-start space-x-3">
            <User className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-orange-700 font-medium">{t('login.authorized_roles_only')}</p>
              <p className="text-sm text-orange-600 mt-1">
                {t('login.authorized_roles_description')}
              </p>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-start space-x-3">
              <div className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5">⚠️</div>
              <div>
                <p className="text-sm text-red-700 font-medium">{t('login.authentication_failed')}</p>
                <p className="text-sm text-red-600 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Login Form */}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Email Input */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auth.login.email')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('auth.login.email_placeholder')}
                      type="email"
                      autoComplete="email"
                      disabled={isLoading}
                      className="px-4 py-3 rounded-xl"
                      {...field}
                    />
                  </FormControl>
                  {/* ✅ FIXED: Use custom error message instead of Zod's default */}
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Password Input */}
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auth.login.password')}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder={t('auth.login.password_placeholder')}
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        disabled={isLoading}
                        className="px-4 py-3 pr-12 rounded-xl"
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={isLoading || isSubmitting} /* ✅ FIXED: Guard against both loading states */
                        className="absolute inset-y-0 right-0 pr-3 flex items-center h-full"
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Login Button */}
            <div className="mt-6">
              <Button
                type="submit"
                disabled={isLoading || isSubmitting} /* ✅ FIXED: Guard against both loading states */
                className="w-full py-3 rounded-xl font-medium"
                size="lg"
              >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin mr-2"></div>
                  <span>{t('auth.login.loading')}</span>
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5 mr-2" />
                  <span>{t('auth.login.submit')}</span>
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>

          {/* {onSwitchToPasswordReset && (
            <div className="text-center">
              <button
                type="button"
                onClick={onSwitchToPasswordReset}
                disabled={isLoading}
                className="text-sm text-primary hover:text-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Forgot your password?
              </button>
            </div>
          )} */}

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-muted-foreground">
            {t('login.contact_admin')}
          </p>
        </div>
      </div>
    </div>
  );
};